import { Link } from '../link';
import { isCrossSite, type NavItem } from '@goodbuild/rules/site';

/**
 * A navigation or footer link, which may leave the site.
 *
 * The three sites are separate origins, so a cross-site entry is a plain
 * anchor: `next/link` prefetches and navigates on the client, and neither is
 * possible across an origin. Getting this wrong produces a link that appears to
 * work and then silently does nothing.
 *
 * In its own file rather than in `ui.tsx` because the footer needs it and
 * `ui.tsx` imports the footer — putting it there makes the two modules import
 * each other, which happens to work today and breaks the first time either one
 * grows a top-level side effect.
 */
export function ChromeLink({
  item,
  className,
  current = false,
}: {
  item: NavItem;
  /**
   * `| undefined` explicitly, because `exactOptionalPropertyTypes` is on: a
   * caller computing the class from a condition passes `undefined` for "no
   * class", and without this that is a type error at every such call site.
   */
  className?: string | undefined;
  /**
   * Whether this entry is the page being looked at.
   *
   * **Separate from `className`, and #113 is why it had to be.** The marker in
   * the top bar was a class and nothing else: `.navCurrent` paints a colour and
   * a rule, `SiteNav` passed it, and a colour is not available to somebody
   * using a screen reader. So the bar had a current-page marker that half its
   * readers could not perceive, on every page of both applications.
   *
   * `WorkNav` has said `aria-current="page"` since the rail was written, which
   * is what makes this a gap rather than a decision: the same information, said
   * one way down the side of the page and not at all across the top of it. This
   * prop is what lets the bar say it too, and it is deliberately a boolean
   * rather than the attribute — a caller passing `aria-current` directly could
   * pass `'true'`, `'location'` or `'step'`, and there is one right answer for
   * a navigation entry naming the page you are on.
   *
   * Defaulted rather than optional at the use site, so the footer and every
   * other caller that has no idea which page this is go on rendering exactly
   * what they rendered before.
   */
  current?: boolean | undefined;
}): React.ReactElement {
  /*
   * Built once and spread into both branches. Written inline in each, the
   * anchor and the `Link` are two places to get the same attribute wrong, and
   * the cross-site branch is the one nobody looks at — it is reached only when
   * the store's bar names one of the app's paths, which is most of the bar on
   * one of the two applications and none of it on the other.
   *
   * Spread rather than `aria-current={current ? 'page' : undefined}` because
   * `exactOptionalPropertyTypes` is on and `LinkProps` declares the attribute
   * as `'page' | undefined`; an explicit `undefined` would also put a dead
   * attribute on the DOM element in the anchor branch.
   */
  const marker = current ? ({ 'aria-current': 'page' } as const) : {};

  if (isCrossSite(item.href)) {
    return (
      <a href={item.href} className={className} {...marker}>
        {item.label}
      </a>
    );
  }

  return (
    <Link href={item.href} className={className} {...marker}>
      {item.label}
    </Link>
  );
}
